/**
 * CivicPulse — Home / landing page.
 *
 * Adheres to the mockup: hero on left, live map on right, geo/category
 * pickers below, category counter tiles, recently-resolved strip.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, MapPin, MessageSquare, Users, CheckCircle2, Camera,
  Sparkles, Globe2, ChevronDown,
} from "lucide-react";
import CivicMap, { MapLegend } from "@/components/civic/CivicMap";
import { civic, photoUrl } from "./api";
import { civicPath } from "./basePath";
import { useCivicI18n } from "./useCivicI18n";

const ICON_MAP = {
  potholes:        require("lucide-react").TrafficCone,
  garbage:         require("lucide-react").Trash2,
  flooding:        require("lucide-react").Waves,
  streetlights:    require("lucide-react").Lightbulb,
  "traffic-signals": require("lucide-react").AlertTriangle,
  sewage:          require("lucide-react").Droplets,
  "open-manhole":  require("lucide-react").AlertTriangle,
  "exposed-wires": require("lucide-react").Zap,
  other:           require("lucide-react").HelpCircle,
};

const TILE_SLUGS = ["potholes", "garbage", "flooding", "streetlights"];
const KERALA_DISTRICTS = [
  "Thiruvananthapuram", "Kollam", "Pathanamthitta", "Alappuzha", "Kottayam",
  "Idukki", "Ernakulam", "Thrissur", "Palakkad", "Malappuram", "Kozhikode",
  "Wayanad", "Kannur", "Kasaragod",
];

export default function CivicHome() {
  const navigate = useNavigate();
  const { t } = useCivicI18n();
  const [issues, setIssues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [counters, setCounters] = useState({});
  const [totals, setTotals] = useState({ total: 0, resolved: 0 });
  const [recent, setRecent] = useState([]);
  const [loc, setLoc] = useState({ country: "India", state: "Kerala", district: "", city: "", category: "" });

  useEffect(() => {
    civic.categories().then((d) => setCategories(d.items || [])).catch(() => {});
    civic.counters().then((d) => {
      setCounters(d.by_category || {});
      setTotals({ total: d.total, resolved: d.resolved });
    }).catch(() => {});
    civic.listIssues({ limit: 60 }).then((d) => setIssues(d.items || [])).catch(() => {});
    civic.recentResolved(4).then((d) => setRecent(d.items || [])).catch(() => {});
  }, []);

  const categoryColors = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.slug, c.color])),
    [categories]
  );

  const tiles = useMemo(
    () => TILE_SLUGS.map((slug) => categories.find((c) => c.slug === slug)).filter(Boolean),
    [categories]
  );

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-900" data-testid="civic-home">
      {/* ─────────── HERO ─────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-70"
             style={{
               backgroundImage:
                 "radial-gradient(1200px 400px at 10% 0%, #DCFCE7 0%, transparent 60%), radial-gradient(900px 300px at 90% 20%, #E0F2FE 0%, transparent 60%)",
             }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[.22em] text-slate-500">
              {t("home.eyebrow", "Stronger communities. Brighter tomorrows.")}
            </p>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-slate-900">
              {t("home.headline1", "See a problem in your")} <span className="text-emerald-700">{t("home.headline2", "community?")}</span><br/>
              {t("home.headline3", "Put it on the map.")}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-700 max-w-xl leading-relaxed">
              {t("home.lead", "Report local civic problems, connect with the responsible authority, follow the response, and help verify the solution.")}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to={civicPath("/report")}
                data-testid="civic-cta-report"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-6 py-3 shadow-sm transition"
              >
                <Camera className="w-4 h-4" /> {t("home.cta.report", "Report a Problem")}
              </Link>
              <Link
                to={civicPath("/map")}
                data-testid="civic-cta-map"
                className="inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-emerald-700 text-emerald-800 hover:bg-emerald-50 font-semibold px-6 py-3 transition"
              >
                <MapPin className="w-4 h-4" /> {t("home.cta.map", "View Civic Map")}
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg">
              <PillarStat icon={Users}      title={t("home.pillar.report_title", "Citizens report")} sub={t("home.pillar.report_sub", "Real issues")} />
              <PillarStat icon={MessageSquare} title={t("home.pillar.respond_title", "Authorities respond")} sub={t("home.pillar.respond_sub", "Track progress")} />
              <PillarStat icon={CheckCircle2} title={t("home.pillar.communities_title", "Stronger communities")} sub={t("home.pillar.communities_sub", "A cleaner tomorrow")} />
            </div>
          </div>

          {/* Live map card */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden ring-1 ring-emerald-100 shadow-lg bg-white">
              <CivicMap
                issues={issues}
                categoryColors={categoryColors}
                height={420}
                zoom={7}
              />
              <div className="absolute top-3 right-3 w-56 z-[400]">
                <MapLegend categories={categories.slice(0, 8)} counters={counters} />
              </div>
              <div className="absolute bottom-3 left-3 z-[400] bg-white/95 backdrop-blur px-3 py-2 rounded-full text-xs text-emerald-800 ring-1 ring-emerald-100 shadow-sm">
                {totals.total} {t("home.map.issues_on_map", "issues on the map")} • {totals.resolved} {t("home.map.resolved", "resolved")}
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-500 text-right italic">
              {t("home.map.footer", "Click any pin to open the issue → follow, comment, verify.")}
            </p>
          </div>
        </div>

        {/* ─────────── SELECTOR BAR ─────────── */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2 pb-6">
          <div className="bg-white ring-1 ring-emerald-100 rounded-2xl shadow-sm p-4 sm:p-5 grid gap-3 md:grid-cols-6 items-end">
            <Selector icon={Globe2} label="Country" value={loc.country}
                      options={["India"]} onChange={(v) => setLoc({ ...loc, country: v })}
                      testid="civic-sel-country" />
            <Selector icon={MapPin} label="State / Province" value={loc.state}
                      options={["Kerala"]} onChange={(v) => setLoc({ ...loc, state: v })}
                      testid="civic-sel-state" />
            <Selector icon={MapPin} label="District" value={loc.district || "Select district"}
                      options={["Select district", ...KERALA_DISTRICTS]}
                      onChange={(v) => setLoc({ ...loc, district: v === "Select district" ? "" : v })}
                      testid="civic-sel-district" />
            <Selector icon={MapPin} label="City / Town" value={loc.city || "Any city"}
                      options={["Any city"]}
                      onChange={(v) => setLoc({ ...loc, city: v === "Any city" ? "" : v })}
                      testid="civic-sel-city" />
            <Selector icon={Sparkles} label="Category"
                      value={loc.category ? categories.find((c) => c.slug === loc.category)?.label_en || loc.category : "All categories"}
                      options={["All categories", ...categories.map((c) => c.label_en)]}
                      onChange={(v) => {
                        if (v === "All categories") setLoc({ ...loc, category: "" });
                        else setLoc({ ...loc, category: categories.find((c) => c.label_en === v)?.slug || "" });
                      }}
                      testid="civic-sel-category" />
            <button
              data-testid="civic-view-map-btn"
              onClick={() => {
                const geoParts = [(loc.country || "").toLowerCase(), (loc.state || "").toLowerCase(),
                                  loc.district && loc.district.toLowerCase(), loc.city && loc.city.toLowerCase()]
                  .filter(Boolean).join("/");
                const path = civicPath(geoParts ? `/${geoParts}` : "/map");
                const qs = new URLSearchParams();
                if (loc.category) qs.set("category", loc.category);
                navigate(`${path}${qs.toString() ? `?${qs}` : ""}`);
              }}
              className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-3 flex items-center justify-center gap-2 transition"
            >
              <MapPin className="w-4 h-4" /> View Civic Map
            </button>
          </div>
        </div>
      </section>

      {/* ─────────── CATEGORY COUNTER TILES ─────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-4 md:grid-cols-5">
        {tiles.map((c) => {
          const Icon = ICON_MAP[c.slug] || ICON_MAP.other;
          return (
            <Link
              key={c.slug}
              to={civicPath(`/map?category=${c.slug}`)}
              data-testid={`civic-tile-${c.slug}`}
              className="group rounded-2xl bg-white ring-1 ring-emerald-100 hover:ring-emerald-300 hover:shadow-md transition p-5 flex items-center gap-4"
            >
              <span
                className="grid place-items-center w-14 h-14 rounded-2xl"
                style={{ background: `${c.color}18`, color: c.color }}
              >
                <Icon className="w-7 h-7" strokeWidth={2.1} />
              </span>
              <div>
                <div className="font-semibold text-slate-800">{c.label_en}</div>
                <div className="text-2xl font-serif font-bold text-slate-900 tabular-nums leading-none mt-1">
                  {(counters[c.slug] || 0).toLocaleString()}
                </div>
                <div className="text-xs text-slate-500 mt-1">reported issues</div>
              </div>
              <ArrowRight className="w-4 h-4 ml-auto text-emerald-600 opacity-0 group-hover:opacity-100 transition" />
            </Link>
          );
        })}
        {/* Recently resolved */}
        <div className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 p-5">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold">
            <CheckCircle2 className="w-5 h-5" /> Recently Resolved
            <Link to={civicPath("/resolved")} className="ml-auto text-xs underline hover:no-underline">View All →</Link>
          </div>
          <div className="mt-3 space-y-3">
            {recent.slice(0, 2).map((r) => (
              <Link to={civicPath(`/issue/${r.public_id}`)} key={r.public_id}
                    className="block text-sm">
                <div className="font-semibold text-slate-800 truncate">{r.title}</div>
                <div className="text-xs text-emerald-700">✓ Verified Resolved</div>
                <div className="text-xs text-slate-500 truncate">{r.location?.address || `${r.location?.city || ""}, ${r.location?.state || ""}`}</div>
              </Link>
            ))}
            {recent.length === 0 && (
              <div className="text-sm text-emerald-800/70 italic">First resolutions will appear here.</div>
            )}
          </div>
        </div>
      </section>

      {/* ─────────── FOOTER TAGLINES ─────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-14 mb-4 grid gap-6 md:grid-cols-3 text-sm text-slate-600">
        <TagRow icon={Users} title="Real People" sub="Real problems" />
        <TagRow icon={MessageSquare} title="Today's reports" sub="Brighter tomorrows" />
        <TagRow icon={CheckCircle2} title="Civic Action" sub="Stronger communities" />
      </section>
    </div>
  );
}

function PillarStat({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-2">
      <span className="grid place-items-center w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div className="leading-tight">
        <div className="text-xs font-semibold text-slate-800">{title}</div>
        <div className="text-[11px] text-slate-500">{sub}</div>
      </div>
    </div>
  );
}

function Selector({ icon: Icon, label, value, options, onChange, testid }) {
  return (
    <label className="block text-sm">
      <div className="flex items-center gap-2 text-slate-600 mb-1">
        <Icon className="w-4 h-4 text-emerald-700" /> <span className="font-medium">{label}</span>
      </div>
      <div className="relative">
        <select
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-xl border border-slate-200 bg-white pl-3 pr-9 py-2.5 text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          {options.map((o) => <option key={o}>{o}</option>)}
        </select>
        <ChevronDown className="pointer-events-none w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
      </div>
    </label>
  );
}

function TagRow({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid place-items-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <div className="font-semibold text-slate-800">{title}</div>
        <div className="text-xs">{sub}</div>
      </div>
    </div>
  );
}
